/**
 * Google Tag Manager (GTM) DataLayer utilities
 * Following official GTM documentation: https://developers.google.com/tag-platform/tag-manager/datalayer
 */

/* eslint-disable import/prefer-default-export */

import { getUUID } from './utils';

/**
 * Generates a unique event ID for each dataLayer push.
 * Uses crypto.randomUUID when available, falls back to a timestamp-based ID.
 */
export const generateEventId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/** GA4 session id from _ga_<measurementId> cookie (G-JJHCBQLQ4R → _ga_JJHCBQLQ4R). */
const getGa4SessionIdFromCookie = () => {
  try {
    const raw = document.cookie.match(/(?:^|;\s*)_ga_JJHCBQLQ4R=([^;]+)/);
    if (!raw) return undefined;
    const gs2 = raw[1].match(/^GS2\.\d+\.s(\d+)/);
    const gs1 = raw[1].match(/^GS1\.\d+\.(\d+)/);
    let id = NaN;
    if (gs2) id = parseInt(gs2[1], 10);
    else if (gs1) id = parseInt(gs1[1], 10);
    return Number.isFinite(id) ? id : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Pushes a select_deal event to the GTM dataLayer
 * @param {Object} dealData - The deal/reward data
 * @param {string} dealData.uid - The deal ID
 * @param {Object} dealData.store - The store/partner information
 * @param {string} dealData.store.title - The partner name
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 * @param {number} index - The position of the deal in the list (0-based)
 */
export const pushSelectDealEvent = (dealData, userData, index = 0) => {
  // Initialize dataLayer array if it doesn't exist (GTM best practice)
  window.dataLayer = window.dataLayer || [];

  // Clear previous ecommerce object (required for ecommerce events)
  window.dataLayer.push({ ecommerce: null });

  // Push the select_deal event
  window.dataLayer.push({
    event: 'select_deal',
    event_id: generateEventId(),
    user_id: getUUID(userData) || '',
    ecommerce: {
      item_list_name: 'Deal Browser',
      items: [{
        item_id: dealData?.uid || '',
        item_brand: dealData?.store?.title || '',
        index,
      }],
    },
  });
};

/**
 * Pushes a subscription_started event to the GTM dataLayer
 * Triggered when user clicks "GO UNLIMITED" button
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushSubscriptionStartedEvent = (userData) => {
  window.dataLayer = window.dataLayer || [];

  // Use normalized affiliate_cid (matches GA4, PostHog, backend)
  const userId = getUUID(userData) || '';

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'subscription_started',
    event_id: generateEventId(),
    user_id: userId,
  });
};

/**
 * Membership line-item identity, shared by the AddToCart dataLayer push and the
 * browser InitiateCheckout so both report the same product to Meta.
 *
 * `item_name` is sent from here on purpose. GTM tags 355/367 used to hardcode it
 * (misspelt "Suscription"); MemoLogs asked for it to come from the push instead
 * so the name is fixed in one place. Related: `MEMBERSHIP_ITEM_ID` is what Meta
 * receives as `content_ids`.
 */
export const MEMBERSHIP_ITEM_ID = 'trashie-unlimited';
export const MEMBERSHIP_ITEM_NAME = 'Trashie Unlimited Subscription';

/**
 * Pushes an add_to_cart event for the membership "Checkout Now" CTA.
 *
 * The membership journey has no cart page — it goes app.trashie.io straight to
 * the Shopify hosted checkout — so this click is the only cart-add signal in the
 * funnel, and Shopify's own pixel cannot see it. Requested by MemoLogs for
 * campaign optimisation.
 *
 * `item_category: 'subscription'` on items[0] is deliberate: it is what routes
 * this to the membership-specific GTM tags rather than the generic storefront
 * ones.
 *
 * @param {Object} params
 * @param {number|null} params.value - Membership charge in dollars; omitted when
 *   the plan has not loaded, rather than sending the display fallback as if real.
 * @param {string} params.currency - ISO currency code.
 * @param {Array<{productType: string, quantity: number}>} params.products -
 *   Starter-kit lines. Free member entitlements, so they carry price 0.
 * @param {Object} params.userData - For the normalized user id.
 * @return {string} The event_id used, so a server-side counterpart can dedup.
 */
export const pushMembershipAddToCartEvent = ({
  value,
  currency = 'USD',
  products = [],
  userData,
} = {}) => {
  window.dataLayer = window.dataLayer || [];

  // `atc_` prefix mirrors the `ic_` convention on the checkout event, so an id
  // is identifiable by event type when reconciling against Meta.
  const eventId = `atc_${generateEventId()}`;
  const hasValue = typeof value === 'number' && value >= 0;

  const items = [
    {
      item_id: MEMBERSHIP_ITEM_ID,
      item_name: MEMBERSHIP_ITEM_NAME,
      item_category: 'subscription',
      ...(hasValue && { price: value }),
      quantity: 1,
    },
    // Starter-kit selections, after the membership so items[0] stays the product
    // Meta reads for content_ids / content_name.
    ...(products || [])
      .filter((product) => product?.productType)
      .map((product) => ({
        item_id: product.productType,
        item_name: product.productType,
        item_category: 'starter_kit',
        price: 0,
        quantity: product.quantity || 1,
      })),
  ];

  // Clearing ecommerce first is required: GTM's dataLayer model persists, so a
  // previous ecommerce object would merge into this one.
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'add_to_cart',
    event_id: eventId,
    user_id: getUUID(userData) || '',
    // Top-level as well as inside ecommerce — the membership GA4 tag reads the
    // top-level key, the generic one reads ecommerce.currency.
    currency,
    ecommerce: {
      currency,
      ...(hasValue && { value }),
      items,
    },
  });

  return eventId;
};

/**
 * Pushes a purchase event for subscription checkout to the GTM dataLayer.
 * event_id matches the Stripe checkout session ID so Meta CAPI browser + server dedup aligns.
 * @param {Object} params
 * @param {string} params.transactionId - Stripe checkout session ID
 * @param {number} params.value - Amount paid
 * @param {string} params.currency - ISO currency code
 * @param {string} params.itemName - Product line item name
 * @param {number} params.discount - Discount amount (0 if none)
 * @param {Object} params.userData - User data (affiliate_cid for tracking)
 * @param {string} params.email - User email for Meta Advanced Matching
 */
export const pushSubscriptionPurchaseEvent = ({
  transactionId,
  value,
  currency,
  itemName,
  discount,
  email,
  userData,
}) => {
  if (!transactionId) return;
  const eventId = transactionId;
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'purchase',
    event_id: eventId,
    user_id: getUUID(userData) || '',
    ecommerce: {
      transaction_id: transactionId,
      value,
      currency,
      items: [
        {
          item_id: transactionId,
          item_name: itemName,
          item_category: 'subscription',
          price: value,
          discount,
          quantity: 1,
        },
      ],
    },
    user_data: {
      email: email || '',
    },
  });

  // Send directly to GA4 via gtag() so ga.js processes the event in the
  // current browser session (which already has campaign attribution from the
  // landing page). Tag 366 is paused so the dataLayer push above won't reach
  // GA4 through GTM. Server-side MP also sends this event but MP cannot set
  // session-level traffic source — this browser-side call can.
  // GA4 deduplicates by transaction_id.
  if (typeof window.gtag === 'function') {
    const sessionId = getGa4SessionIdFromCookie();
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value,
      currency,
      purchase_type: 'membership',
      ...(sessionId !== undefined ? { session_id: sessionId } : {}),
      engagement_time_msec: 100,
      items: [
        {
          item_id: transactionId,
          item_name: itemName,
          item_category: 'subscription',
          price: value,
          discount,
          quantity: 1,
        },
      ],
    });
  }
};

/**
 * Pushes a cancellation_started event to the GTM dataLayer
 * Triggered when user enters the cancel subscription flow
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushCancellationStartedEvent = (userData) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'cancellation_started',
    event_id: generateEventId(),
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a subscription_cancelled event to the GTM dataLayer
 * Triggered when the subscription cancellation is successfully completed
 * @param {Object} params
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 * @param {string} params.cancelReason - The radio-selected reason (e.g. 'Not saving enough')
 * @param {string} params.cancelComment - Optional free-text comment from the user
 */
export const pushSubscriptionCancelledEvent = ({ userData, cancelReason, cancelComment }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'subscription_cancelled',
    event_id: generateEventId(),
    cancel_reason: cancelReason || '',
    cancel_comment: cancelComment || '',
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a sign_up event to the GTM dataLayer
 * Triggered when a new user completes registration
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 * @param {string} method - The signup method ('email' | 'google' | 'apple')
 */
export const pushSignUpEvent = (userData, method) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'sign_up',
    event_id: generateEventId(),
    method: method || '',
    user_id: getUUID(userData) || '',
    trashiecash: userData?.balance?.currentBalance ?? 0,
  });
};

/**
 * Pushes a login event to the GTM dataLayer
 * Triggered when an existing user logs in
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 * @param {string} method - The login method ('email' | 'google' | 'apple')
 */
export const pushLoginEvent = (userData, method) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'login',
    event_id: generateEventId(),
    method: method || '',
    user_id: getUUID(userData) || '',
    trashiecash: userData?.balance?.currentBalance ?? 0,
  });
};

/**
 * Pushes a deal_redirect event to the GTM dataLayer
 * Triggered before redirecting the user to a partner site
 * @param {Object} params
 * @param {Object} params.rewardItem - The reward/deal data
 * @param {string} params.destinationUrl - The URL the user is being redirected to
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushDealRedirectEvent = ({ rewardItem, destinationUrl, userData }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'deal_redirect',
    event_id: generateEventId(),
    deal_id: rewardItem?.uid || '',
    partner_name: rewardItem?.store?.title || '',
    item_name: rewardItem?.name || '',
    item_category: rewardItem?.type || '',
    destination_url: destinationUrl || '',
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a coupon_revealed event to the GTM dataLayer
 * Triggered when a user redeems a deal/reward and the coupon is revealed
 * @param {Object} params
 * @param {Object} params.rewardItem - The reward/deal data
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushCouponRevealedEvent = ({ rewardItem, userData }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'coupon_revealed',
    event_id: generateEventId(),
    deal_id: rewardItem?.uid || '',
    partner_name: rewardItem?.store?.title || '',
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a gift_card_redeemed event to the GTM dataLayer
 * Triggered when a user successfully redeems a gift card
 * @param {Object} params
 * @param {string} params.giftCardName - The gift card store name
 * @param {number} params.giftCardValue - The gift card dollar value
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushGiftCardRedeemedEvent = ({ giftCardName, giftCardValue, userData }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'gift_card_redeemed',
    event_id: generateEventId(),
    gift_card: giftCardName || '',
    gift_card_value: giftCardValue || 0,
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a bag_registered event to the GTM dataLayer
 * Triggered when a user finalizes TBB registration
 * @param {Object} params
 * @param {string} params.bagId - The redemption code (unique bag identifier)
 * @param {string} params.bagType - The bag type (e.g., 'standard', 'kids', 'tech_box')
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 * @param {string} params.courier - The shipping courier name (e.g., 'FedEx', 'USPS')
 */
export const pushBagRegisteredEvent = ({ bagId, bagType, userData, courier }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'bag_registered',
    event_id: generateEventId(),
    bag_id: bagId || '',
    bag_type: bagType || '',
    user_id: getUUID(userData) || '',
    registration_date: new Date().toISOString().split('T')[0],
    courier: courier || '',
  });
};

/**
 * Pushes an account_drawer_viewed event to the GTM dataLayer.
 * Triggered when the My Account drawer becomes visible.
 */
export const pushAccountDrawerViewedEvent = () => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'account_drawer_viewed',
    event_id: generateEventId(),
  });
};

/**
 * Pushes a package_tracker_drawer_viewed event to the GTM dataLayer.
 * Triggered when the package tracker drawer is viewed for a stage.
 * @param {Object} params
 * @param {string|number} params.stage - Viewed stage (1-5 or 'lost')
 */
export const pushPackageTrackerDrawerViewedEvent = ({ stage }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'package_tracker_drawer_viewed',
    event_id: generateEventId(),
    stage: stage ?? '',
  });
};
